import React, { useState } from 'react';
import { SaveIcon, XIcon } from 'lucide-react';
import type { Goal, MetricType, TimeframeType } from '../../types';
interface GoalFormProps {
  goal?: Goal;
  onSave: (goal: Goal) => void;
  onCancel: () => void;
}
export function GoalForm({
  goal,
  onSave,
  onCancel
}: GoalFormProps) {
  const [metric, setMetric] = useState<MetricType>(goal?.metric ?? 'softPull');
  const [timeframe, setTimeframe] = useState<TimeframeType>(goal?.timeframe ?? 'weekly');
  const [target, setTarget] = useState<number>(goal?.target ?? 10);
  const [assignmentLevel, setAssignmentLevel] = useState<Goal['assignmentLevel']>(goal?.assignmentLevel ?? 'company');
  const [assignmentId, setAssignmentId] = useState<string>(goal?.assignmentId ?? '');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newGoal: Goal = {
      id: goal?.id ?? `goal-${Date.now()}`,
      metric,
      timeframe,
      target,
      assignmentLevel,
      assignmentId: assignmentLevel !== 'company' ? assignmentId : undefined,
      effectiveDate: goal?.effectiveDate ?? new Date().toISOString().split('T')[0]
    };
    onSave(newGoal);
  };
  return <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-131">
      <h3 className="text-lg font-semibold text-gray-900 mb-5" data-id="element-132">
        {goal ? 'Edit Goal' : 'Add New Goal'}
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-133">
        {/* Metric */}
        <div data-id="element-134">
          <label htmlFor="goal-metric" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-135">
            Metric
          </label>
          <select id="goal-metric" value={metric} onChange={e => setMetric(e.target.value as MetricType)} className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" data-id="element-136">
            <option value="softPull" data-id="element-137">Soft Pull</option>
            <option value="submitted" data-id="element-138">Submitted</option>
          </select>
        </div>

        {/* Timeframe */}
        <div data-id="element-139">
          <label htmlFor="goal-timeframe" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-140">
            Timeframe
          </label>
          <select id="goal-timeframe" value={timeframe} onChange={e => setTimeframe(e.target.value as TimeframeType)} className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" data-id="element-141">
            <option value="weekly" data-id="element-142">Weekly</option>
            <option value="monthly" data-id="element-143">Monthly</option>
          </select>
        </div>

        {/* Target */}
        <div data-id="element-144">
          <label htmlFor="goal-target" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-145">
            Target
          </label>
          <input id="goal-target" type="number" min={1} value={target} onChange={e => setTarget(parseInt(e.target.value, 10) || 0)} className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" data-id="element-146" />
        </div>

        {/* Assignment Level */}
        <div data-id="element-147">
          <label htmlFor="goal-assignment" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-148">
            Assignment Level
          </label>
          <select id="goal-assignment" value={assignmentLevel} onChange={e => setAssignmentLevel(e.target.value as Goal['assignmentLevel'])} className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors" data-id="element-149">
            <option value="company" data-id="element-150">Company-Wide</option>
            <option value="division" data-id="element-151">Division</option>
            <option value="team" data-id="element-152">Team</option>
            <option value="loanOfficer" data-id="element-153">Loan Officer</option>
          </select>
        </div>

        {/* Assignment ID (conditional) */}
        {assignmentLevel !== 'company' && <div className="sm:col-span-2" data-id="element-154">
            <label htmlFor="goal-assignment-id" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-155">
              Assignment ID
            </label>
            <input id="goal-assignment-id" type="text" value={assignmentId} onChange={e => setAssignmentId(e.target.value)} placeholder={`Enter ${assignmentLevel} ID`} className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors placeholder:text-gray-400" data-id="element-156" />
          </div>}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 mt-6 pt-5 border-t border-gray-100" data-id="element-157">
        <button type="button" onClick={onCancel} className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-158">
          <XIcon className="w-4 h-4" data-id="element-159" />
          Cancel
        </button>
        <button type="submit" className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm" data-id="element-160">
          <SaveIcon className="w-4 h-4" data-id="element-161" />
          {goal ? 'Update Goal' : 'Save Goal'}
        </button>
      </div>
    </form>;
}